import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Shield, CheckCircle, Star, MessageCircle, Scale, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { siteConfig, services } from '@/data/mock';
import { getTestimonials } from '@/services/api';

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const featuredServices = services.slice(0, 3);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const data = await getTestimonials();
      setTestimonials(data.slice(0, 3));
    } catch (error) {
      console.error('Error loading testimonials:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      MessageCircle: MessageCircle,
      Scale: Scale,
      Heart: Heart,
    };
    const Icon = icons[iconName] || MessageCircle;
    return <Icon className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(210,40%,98%)] via-transparent to-white" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Hero Content */}
            <div className="animate-fade-in">
              {/* Trust Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
                <Shield className="w-4 h-4 mr-2" />
                Kostenlose & vertrauliche Beratung
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[hsl(215,25%,15%)] leading-tight mb-6">
                Unterstützung für Väter in
                <span className="text-[hsl(213,80%,35%)]"> schwierigen Zeiten</span>
              </h1>

              <p className="text-lg md:text-xl text-[hsl(215,15%,45%)] leading-relaxed mb-8 max-w-xl">
                Sie stehen vor einem Scherbenhaufen? Ich kenne diese Situation aus eigener Erfahrung. 
                Lassen Sie uns gemeinsam einen Weg finden.
              </p>

              {/* Key Points */}
              <div className="space-y-3 mb-8">
                {[
                  'Kostenlose Erstberatung ohne Verpflichtung',
                  'Vermittlung zu spezialisierten Anwälten',
                  'Praktische Tipps aus eigener Erfahrung'
                ].map((point, index) => (
                  <div key={index} className="flex items-center text-[hsl(215,25%,30%)]">
                    <CheckCircle className="w-5 h-5 text-[hsl(160,60%,40%)] mr-3 flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white shadow-lg hover:shadow-xl transition-all text-base px-8">
                  <Link to="/kontakt">
                    Kostenloses Gespräch vereinbaren
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-[hsl(213,80%,35%)] text-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,35%,0.05)] text-base">
                  <a href={`tel:${siteConfig.phone}`}>
                    <Phone className="w-5 h-5 mr-2" />
                    Jetzt anrufen
                  </a>
                </Button>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative animate-fade-in delay-200 hidden lg:block">
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[hsl(210,25%,90%)]">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 rounded-xl bg-[hsl(213,80%,35%)] flex items-center justify-center mr-4">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[hsl(215,25%,15%)]">Väternotruf</h3>
                      <p className="text-[hsl(215,15%,45%)]">Ihr Partner in der Krise</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-[hsl(210,40%,98%)] rounded-lg">
                      <p className="text-sm text-[hsl(215,15%,45%)] mb-1">Nächster freier Termin</p>
                      <p className="font-semibold text-[hsl(215,25%,15%)]">Innerhalb von 48 Stunden</p>
                    </div>
                    <div className="p-4 bg-[hsl(160,60%,95%)] rounded-lg border border-[hsl(160,60%,80%)]">
                      <p className="text-sm text-[hsl(160,60%,30%)] font-medium flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        100% kostenlos & vertraulich
                      </p>
                    </div>
                  </div>
                </div>

                {/* Floating Stats */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-[hsl(210,25%,90%)] animate-float">
                  <p className="text-2xl font-bold text-[hsl(213,80%,35%)]">500+</p>
                  <p className="text-sm text-[hsl(215,15%,45%)]">Vätern geholfen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-[hsl(35,90%,95%)] border-y border-[hsl(35,70%,80%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center text-center">
            <Shield className="w-5 h-5 text-[hsl(35,90%,40%)] mr-3 flex-shrink-0" />
            <p className="text-sm text-[hsl(35,50%,30%)]">
              <strong>Wichtig:</strong> Diese Beratung ersetzt keine Rechtsberatung. Ich bin kein Anwalt.
            </p>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Wie ich Ihnen helfen kann
            </h2>
            <p className="text-lg text-[hsl(215,15%,45%)] max-w-2xl mx-auto">
              Praktische Unterstützung und Orientierung für Väter in schwierigen Familiensituationen
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <Card 
                key={service.id} 
                className="card-hover border-[hsl(210,25%,90%)] bg-white animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mb-6">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-[hsl(215,15%,45%)] leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="border-[hsl(213,80%,35%)] text-[hsl(213,80%,35%)]">
              <Link to="/leistungen">
                Alle Leistungen ansehen
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Was andere Väter sagen
            </h2>
            <p className="text-lg text-[hsl(215,15%,45%)] max-w-2xl mx-auto">
              Erfahrungen von Vätern, die den Weg bereits gegangen sind
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id} 
                className="card-hover border-[hsl(210,25%,90%)] bg-white"
              >
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[hsl(35,90%,50%)] fill-current" />
                    ))}
                  </div>
                  <p className="text-[hsl(215,25%,30%)] leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center mr-3">
                      <span className="text-[hsl(213,80%,35%)] font-semibold">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[hsl(215,25%,15%)]">{testimonial.name}</p>
                      <p className="text-sm text-[hsl(215,15%,45%)]">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[hsl(213,80%,35%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Lassen Sie uns sprechen
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Der erste Schritt ist oft der schwerste. Aber Sie müssen ihn nicht alleine gehen. 
            Vereinbaren Sie jetzt ein kostenloses und unverbindliches Gespräch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[hsl(213,80%,35%)] hover:bg-white/90 shadow-lg text-base px-8">
              <Link to="/kontakt">
                Termin vereinbaren
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white/10 text-base">
              <a href={`tel:${siteConfig.phone}`}>
                <Phone className="w-5 h-5 mr-2" />
                {siteConfig.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
